import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { RegistryClient } from '@/community/client';
import type { PublishedCatalogModel } from '@/community/PublishedCatalogModel';
import type { FavoriteStateModel } from '@/community/FavoriteStateModel';
import type { FavoriteCatalogModel } from '@/community/FavoriteCatalogModel';
import type { ChallengeDiscoveryViewModel } from '@/challenges/ChallengeDiscoveryViewModel';
export interface DiscoverySession {
  client: RegistryClient | null;
  catalog: PublishedCatalogModel;
  favorites: FavoriteCatalogModel;
  favoriteState: FavoriteStateModel;
  library: ChallengeDiscoveryViewModel;
}
const Scope = createContext<{
  acquire(create: () => DiscoverySession): DiscoverySession;
} | null>(null);
/** App-scoped memory only. Route return preserves summaries/filters/scroll, never uploads. */
export function DiscoverySessionProvider({ children }: { children: ReactNode }) {
  const scope = useMemo(() => {
    let session: DiscoverySession | undefined;
    return {
      acquire(create: () => DiscoverySession) {
        return (session ??= create());
      },
    };
  }, []);
  return <Scope.Provider value={scope}>{children}</Scope.Provider>;
}
export function useDiscoverySession(create: () => DiscoverySession) {
  const scope = useContext(Scope);
  return useMemo(() => (scope ? scope.acquire(create) : create()), [scope]);
}
